/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { isBlank } from '@ember/utils';
import { Channel, Entities, Measure, Grouping, REVERSE_PERIOD_KEYS } from 'cio/utils/metrics/common';
import EnvironmentService from 'cio/services/environment';
import MetricsService from 'cio/services/metrics';
import CampaignModel from 'cio/models/campaign';
import TagModel from 'cio/models/tag';
import { summarizeMetrics } from 'cio/utils/metrics/models/metric';
import { deserializeCampaignChannelMetrics } from 'cio/utils/metrics/deserialize/metric';
import { RESTRICTED_ACTION_TYPES } from 'cio/models/campaign';
import { ActionType } from 'cio/models/action';
import { MetricDenominator } from 'cio/models/environment';
import moment from 'moment-timezone';
import { trackedFunction } from 'ember-resources/util/function';
import { DateRange } from '@customerio/fly-components/utils/fly-date-picker-utils';

interface CampaignsTableRowSignature {
  Args: {
    resolution: Grouping;
    range: DateRange;
    measures: Measure[];
    enabledChannels: Channel[];
    campaign: CampaignModel;
    onTagClicked: (tag: TagModel) => unknown;
    afterArchiveCampaignAction: (campaign: CampaignModel) => unknown;
    afterDeleteCampaignAction: (campaign: CampaignModel) => unknown;
    afterUnarchiveCampaignAction: (campaign: CampaignModel) => unknown;
  };
  Element: HTMLTableRowElement;
}

export default class CampaignsTableRow extends Component<CampaignsTableRowSignature> {
  @service environment!: EnvironmentService;
  @service('metrics') metricsService!: MetricsService;

  constructor(owner: unknown, args: CampaignsTableRowSignature['Args']) {
    super(owner, args);
  }

  metric = trackedFunction(this, () => {
    let { channelMetrics: rawChannelMetrics, id } = this.args.campaign;

    let processedChannelMetrics = deserializeCampaignChannelMetrics(
      { channel_metrics: rawChannelMetrics },
      id,
      this.period,
      this.args.resolution,
      this.period,
      this.metricTzRange,
      this.metricTzRange,
      { version: 2, campaignId: id, environmentId: this.environment.current.id },
    );

    if (processedChannelMetrics != null) {
      return summarizeMetrics(id, Entities.CAMPAIGN, null, Object.values(processedChannelMetrics));
    }

    return null;
  });

  get selectedMeasures() {
    return this.args.measures?.map((measure) => {
      if ([MetricDenominator.Sent, MetricDenominator.Delivered].includes(measure as MetricDenominator)) {
        return {
          showRate: false,
          value: measure,
          total: this.metric.value?.[measure]?.rawTotal,
          totals: this.metric.value?.[measure]?.totals,
        };
      }

      return {
        showRate: true,
        value: measure,
        rate: this.metric.value?.[measure]?.rawRate,
        rates: this.metric.value?.[measure]?.rates,
      };
    });
  }

  get period() {
    return REVERSE_PERIOD_KEYS[this.args.resolution];
  }

  get multiMetricTooltipData() {
    let { channelMetrics } = this.args.campaign;
    return {
      per_channel_metrics: channelMetrics,
    };
  }

  get availableChannels() {
    let { type } = this.args.campaign;
    let restrictedActionTypes = RESTRICTED_ACTION_TYPES[type] ?? [];

    return this.args.enabledChannels.filter((c) => !restrictedActionTypes.includes(`${c}_action` as ActionType));
  }

  get campaignRouteName() {
    return this.args.campaign.isApiTriggered ? 'broadcasts.broadcast' : 'campaigns.campaign';
  }

  get campaignUndeliverableRouteName() {
    return `${this.campaignRouteName}.overview.${this.args.campaign.isApiTriggered ? 'broadcasts' : 'sent'}`;
  }

  get displayName() {
    let { name } = this.args.campaign;
    if (isBlank(name)) {
      name = '(Untitled)';
    }
    return name;
  }

  get metricTzRange() {
    if (this.args.resolution === Grouping.Hourly) {
      // this is in case user selects Last 24 hours preset
      return {
        start: moment.tz(this.args.range.start, this.metricsService.metricsTz),
        end: moment.tz(this.args.range.end, this.metricsService.metricsTz),
      };
    }
    return {
      start: moment.tz(`${this.args.range.start.format('YYYY-MM-DD')} 00:00:00`, this.metricsService.metricsTz),
      end: moment.tz(`${this.args.range.end.format('YYYY-MM-DD')} 00:00:00`, this.metricsService.metricsTz),
    };
  }
}
